import FastForward from "@mui/icons-material/FastForward";
import FastRewind from "@mui/icons-material/FastRewind";
import Pause from "@mui/icons-material/Pause";
import PlayArrow from "@mui/icons-material/PlayArrow";
import { IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { RefObject, useEffect, useState } from "react";

export const formatMediaTime = (seconds: number): string => {
  if (!Number.isFinite(seconds) || seconds < 0) return "--:--";
  const wholeSeconds = Math.floor(seconds);
  const hours = Math.floor(wholeSeconds / 3600);
  const minutes = Math.floor((wholeSeconds % 3600) / 60);
  const remainder = wholeSeconds % 60;
  const paddedSeconds = String(remainder).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`;
};

type Props = Readonly<{
  // The element useHlsVideo drives. Typed as video because that hook owns the
  // ref; hls.js plays an <audio> element through the same interface.
  mediaRef: RefObject<HTMLVideoElement | null>;
  onSkip: (seconds: number) => void;
  skipSeconds: number;
}>;

/** Transport for the archived radio audio. The app draws its own media controls
 * everywhere else, so the element itself stays chrome-less. */
export const RadioTransport = ({ mediaRef, onSkip, skipSeconds }: Props) => {
  const { t } = useTenantTranslation();
  const [isPlaying, setIsPlaying] = useState(false);
  const [positionSeconds, setPositionSeconds] = useState(0);
  const [durationSeconds, setDurationSeconds] = useState(Number.NaN);
  // Holds the thumb while a drag is in flight, so the seek costs one HLS jump
  // on release rather than one per pointer move.
  const [draggedSeconds, setDraggedSeconds] = useState<number | null>(null);

  useEffect(() => {
    const media = mediaRef.current;
    if (!media) return;

    const readMediaState = () => {
      setIsPlaying(!media.paused);
      setPositionSeconds(media.currentTime);
      setDurationSeconds(media.duration);
    };

    // No initial read: this effect runs before the parent's useHlsVideo attaches
    // hls.js to the element, so every state the transport shows still arrives as
    // an event.
    media.addEventListener("play", readMediaState);
    media.addEventListener("pause", readMediaState);
    media.addEventListener("timeupdate", readMediaState);
    media.addEventListener("loadedmetadata", readMediaState);
    media.addEventListener("durationchange", readMediaState);
    media.addEventListener("ended", readMediaState);
    return () => {
      media.removeEventListener("play", readMediaState);
      media.removeEventListener("pause", readMediaState);
      media.removeEventListener("timeupdate", readMediaState);
      media.removeEventListener("loadedmetadata", readMediaState);
      media.removeEventListener("durationchange", readMediaState);
      media.removeEventListener("ended", readMediaState);
    };
  }, [mediaRef]);

  const isSeekable = Number.isFinite(durationSeconds) && durationSeconds > 0;
  const skipBackLabel = t("radioSkipBack", { seconds: skipSeconds });
  const skipForwardLabel = t("radioSkipForward", { seconds: skipSeconds });

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <audio ref={mediaRef} style={{ display: "none" }} />

      <Tooltip title={skipBackLabel}>
        <IconButton
          size="small"
          aria-label={skipBackLabel}
          onClick={() => {
            onSkip(-skipSeconds);
          }}
        >
          <FastRewind />
        </IconButton>
      </Tooltip>

      <IconButton
        aria-label={isPlaying ? t("pause") : t("play")}
        onClick={() => {
          const media = mediaRef.current;
          if (!media) return;
          if (media.paused) {
            void media.play();
          } else {
            media.pause();
          }
        }}
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          "&:hover": { bgcolor: "primary.dark" },
        }}
      >
        {isPlaying ? <Pause /> : <PlayArrow />}
      </IconButton>

      <Tooltip title={skipForwardLabel}>
        <IconButton
          size="small"
          aria-label={skipForwardLabel}
          onClick={() => {
            onSkip(skipSeconds);
          }}
        >
          <FastForward />
        </IconButton>
      </Tooltip>

      <Typography variant="body2Mono" color="text.secondary">
        {formatMediaTime(draggedSeconds ?? positionSeconds)}
      </Typography>

      <Slider
        size="small"
        aria-label={t("radioPosition")}
        disabled={!isSeekable}
        value={draggedSeconds ?? positionSeconds}
        min={0}
        max={isSeekable ? durationSeconds : 1}
        step={1}
        onChange={(_event, value) => {
          setDraggedSeconds(value);
        }}
        onChangeCommitted={(_event, value) => {
          setDraggedSeconds(null);
          const media = mediaRef.current;
          if (media) media.currentTime = value;
        }}
        sx={{ flex: 1 }}
      />

      <Typography variant="body2Mono" color="text.secondary">
        {formatMediaTime(durationSeconds)}
      </Typography>
    </Stack>
  );
};
